import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "../../../../api/client";
import {
  extractFileNameFromContentDisposition,
  generateFallbackFileName,
} from "../utils/download";

interface DownloadReportParams {
  reportId: string;
}

export interface DownloadReportResult {
  blob: Blob;
  fileName: string;
  size: number;
}

export const useDownloadReport = () => {
  const queryClient = useQueryClient();

  const downloadMutation = useMutation({
    mutationFn: async ({
      reportId,
    }: DownloadReportParams): Promise<DownloadReportResult> => {
      const response = await client.get(`/reports/${reportId}/download`, {
        responseType: "blob",
      });

      let fileName = generateFallbackFileName(reportId);

      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const headerFileName =
          extractFileNameFromContentDisposition(contentDisposition);
        if (headerFileName) {
          fileName = headerFileName;
        }
      }

      if (!response.data || response.data.size === 0) {
        throw new Error("No file data received from server");
      }

      // Create blob and return it directly
      const blob = new Blob([response.data], { type: "application/pdf" });

      return {
        blob,
        fileName,
        size: response.data.size,
      };
    },
    onSuccess: () => {
      // Invalidate and refetch reports to update download status
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["reports", "statistics"] });
    },
  });

  return {
    downloadReportAsync: downloadMutation.mutateAsync,
    isLoading: downloadMutation.isPending,
    error: downloadMutation.error,
    data: downloadMutation.data,
  };
};
